// founder-decision widget — α.7 of the outreach-agent slice.
//
// Rendered inline in the outreach chat dock when the agent's
// AskUserQuestion tool_use is translated into a
// `:::widget[founder-decision]{...}:::` directive (see
// apps/outreach-agent/src/agent-loop.ts). The card shows the agent's
// question + 2–6 option buttons.
//
// Click flow (the load-bearing piece of α.7):
//   1. Founder clicks an option
//   2. `resumeFromDecision` (from OutreachChatContext, provided by ChatDock)
//      POSTs to /admin/outreach/chat/decision with the action + args
//   3. Projector writes outreach.founder.decided (audit)
//   4. Projector forwards to the agent's POST /chat with a
//      `resumeToolResult` body whose `content` is JSON.stringify({action, args})
//   5. Agent's loop replays with that tool_result as the first user
//      message — the model treats it as the continuation of its previous
//      AskUserQuestion turn and emits more SSE
//   6. resumeFromDecision drains the resumed SSE into a NEW assistant
//      message bubble in the dock (use-outreach-chat.ts decides bubble
//      identity; founder-decision doesn't care)
//
// Why a Context rather than direct fetch:
//   Direct fetch would not attach streamed deltas to the chat stream
//   reducer (`reduceAssistant`); the dock would render the resumed
//   stream as an orphan. The Context lets the dock own SSE draining.
//
// Tokens: bg-card, border-border, text-foreground-mute — all real
// tailwind tokens declared in apps/web/src/styles/tailwind.css. No hex.

import { useState } from 'react';
import { useOutreachChatContext } from '../../lib/outreach-chat-context';

interface Option {
  label: string;
  value: string;
}

interface Props {
  tool_use_id: string;
  question: string;
  // Pre-parsed (e.g. from a test or future structured emitter) OR a
  // JSON-encoded string (the canonical path from the directive parser:
  // the agent double-encodes `options` so the attr-value layer is a
  // single JSON string; we parse it here back into the Option[]).
  options: Option[] | string;
  sessionId: string;
  // Debug-only render hook so static tests can render the decided
  // branch without driving the click → fetch path (mirrors
  // CandidateCard's `decidedOverride`).
  decidedOverride?: string;
}

function parseOptions(input: Props['options']): Option[] {
  if (Array.isArray(input)) return input;
  if (typeof input !== 'string') return [];
  try {
    const parsed = JSON.parse(input);
    if (Array.isArray(parsed)) return parsed as Option[];
    return [];
  } catch {
    return [];
  }
}

export function FounderDecision(p: Props) {
  const { tool_use_id, question, sessionId, decidedOverride } = p;
  const options = parseOptions(p.options);
  const [decided, setDecided] = useState<string | null>(
    decidedOverride ?? null,
  );
  const [pending, setPending] = useState(false);
  const { resumeFromDecision } = useOutreachChatContext();

  const click = async (opt: Option) => {
    if (pending || decided) return;
    setPending(true);
    try {
      await resumeFromDecision({
        sessionId,
        tool_use_id,
        action: opt.value,
      });
      setDecided(opt.label);
    } catch (e) {
      // Surface to dev console — keep the card interactive so the
      // founder can retry. The audit event for THIS click did NOT land
      // (resumeFromDecision throws only on the POST failing before the
      // server emitted the event, which means the write didn't happen).
      console.warn('[founder-decision] resume failed:', e);
    } finally {
      setPending(false);
    }
  };

  if (decided) {
    return (
      <div
        data-testid="founder-decision"
        data-state="decided"
        className="my-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground-mute"
      >
        Chose: {decided}.
      </div>
    );
  }

  return (
    <div
      data-testid="founder-decision"
      data-state="idle"
      className="my-2 rounded-md border border-border bg-card p-3"
    >
      <div className="text-sm font-semibold text-foreground">{question}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`fd-opt-${o.value}`}
            onClick={() => click(o)}
            disabled={pending}
            className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
